import React, { createContext, useState, useContext, useEffect, useCallback } from 'react'
import { NICHE } from '../config/niche'
import { DOMAIN_NAME } from '../config/domain'
import { SITE_NAME } from '../config/siteName'
import { LANGUAGE } from '../config/language'

// Import locale files dynamically
import deLocale from '../locales/de.json'
import enLocale from '../locales/en.json'
import frLocale from '../locales/fr.json'

// Map language codes to locale data
const localeMap = {
  de: deLocale,
  en: enLocale,
  fr: frLocale
}

const LocaleContext = createContext()

// Replace placeholders in translations recursively
const replacePlaceholdersInTranslations = (obj, replacements) => {
  if (typeof obj === 'string') {
    let result = obj
    // Replace all placeholders
    for (const [key, value] of Object.entries(replacements)) {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
      result = result.replace(regex, value)
    }
    // Special handling for niche variations
    if (replacements.NICHE) {
      result = result.replace(/\{\{niche\}\}/g, replacements.NICHE.toLowerCase())
      result = result.replace(/\{\{Niche\}\}/g, replacements.NICHE.charAt(0).toUpperCase() + replacements.NICHE.slice(1).toLowerCase())
    }
    return result
  }
  if (Array.isArray(obj)) {
    return obj.map(item => replacePlaceholdersInTranslations(item, replacements))
  }
  if (obj && typeof obj === 'object') {
    const result = {}
    for (const key in obj) {
      result[key] = replacePlaceholdersInTranslations(obj[key], replacements)
    }
    return result
  }
  return obj
}

export const LocaleProvider = ({ children }) => {
  // Use LANGUAGE from config as default, fallback to 'de' if not set
  const defaultLocale = LANGUAGE || 'de'
  const [locale, setLocale] = useState(defaultLocale)
  const [translations, setTranslations] = useState(() => {
    // Initialize with default locale from config
    const localeData = localeMap[defaultLocale] || localeMap['de']
    const replacements = {
      NICHE: NICHE,
      DOMAIN_NAME: DOMAIN_NAME,
      SITE_NAME: SITE_NAME
    }
    return replacePlaceholdersInTranslations(localeData, replacements)
  })

  const loadTranslations = useCallback(async (localeCode) => {
    try {
      // Load locale file based on language code
      const localeData = localeMap[localeCode] || localeMap[defaultLocale] || localeMap['de']
      const replacements = {
        NICHE: NICHE,
        DOMAIN_NAME: DOMAIN_NAME,
        SITE_NAME: SITE_NAME
      }
      const processedTranslations = replacePlaceholdersInTranslations(localeData, replacements)
      setTranslations(processedTranslations)
      localStorage.setItem('locale', localeCode)
    } catch (error) {
      console.error(`Failed to load locale:`, error)
      // Fallback with placeholder replacement
      const replacements = {
        NICHE: NICHE,
        DOMAIN_NAME: DOMAIN_NAME,
        SITE_NAME: SITE_NAME
      }
      const fallbackLocale = localeMap[defaultLocale] || localeMap['de']
      setTranslations(replacePlaceholdersInTranslations(fallbackLocale, replacements))
    }
  }, [defaultLocale])

  useEffect(() => {
    // Always use LANGUAGE from config as the source of truth
    // This ensures the site displays in the configured language (from environment variable)
    // Clear any localStorage override that doesn't match the config
    const savedLocale = localStorage.getItem('locale')
    if (savedLocale && savedLocale !== defaultLocale) {
      // Clear localStorage if it doesn't match config (user might have changed it manually)
      localStorage.removeItem('locale')
    }
    setLocale(defaultLocale)
    loadTranslations(defaultLocale)
  }, [defaultLocale, loadTranslations])

  const changeLocale = (localeCode) => {
    setLocale(localeCode)
    loadTranslations(localeCode)
  }

  const t = (key, params = {}) => {
    const keys = key.split('.')
    let value = translations
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k]
      } else {
        return key
      }
    }

    if (typeof value !== 'string') {
      return key
    }

    // Simple parameter replacement
    return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
      return params[paramKey] !== undefined ? params[paramKey] : match
    })
  }

  return (
    <LocaleContext.Provider value={{ locale, changeLocale, t, translations }}>
      {children}
    </LocaleContext.Provider>
  )
}

export const useLocale = () => {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider')
  }
  return context
}
